/**
 * Scroll-spy for a sticky, anchor-based on-page navigation.
 *
 * Instantiate with any `<nav>` that contains anchor links pointing at `#id`
 * sections on the page. Links whose target is missing are removed; the link
 * matching the closest section in the viewport gets the `active` class, and the
 * URL hash follows the scroll position. Shared by the `sticky-navigation` and
 * `timeline` blocks so the behaviour lives in exactly one place.
 */
export default class StickyNavigation {

	/**
	 * @param {HTMLElement} navigation The navigation element.
	 */
	constructor( navigation ) {
		if ( ! navigation ) {
			return;
		}

		this.navigation = navigation;
		this.links = this.navigation.getElementsByTagName( 'a' );
		this.elements = [];
		this.windowWidth = window.innerWidth;

		this.validateLinks();
		this.checkHorizontalScroll();
		this.updateActiveLink();
		this.windowEvents();
	}

	/**
	 * The user's motion preference decides whether hash jumps animate.
	 *
	 * @return {ScrollBehavior} 'auto' when the user asked for reduced motion.
	 */
	getScrollBehavior() {
		return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ? 'auto' : 'smooth';
	}

	/**
	 * Validate the links and disable those which don't have an element to scroll to.
	 * @return {void}
	 */
	validateLinks() {
		this.links = Array.from( this.links ).filter( ( link ) => {
			const id = this.getIdFromUrl( link.href );
			const element = document.getElementById( id );

			if ( ! id || ! element ) {
				// Drop the whole list item so no empty <li> is announced.
				( link.closest( 'li' ) || link ).remove();
				return false;
			}

			this.elements.push( element );

			return true;
		} );
	}

	/**
	 * Return the ID from a URL.
	 *
	 * @param {string} url The URL to extract the hash from.
	 * @return {null|string}
	 */
	getIdFromUrl( url ) {
		const hashIndex = url.indexOf( '#' );
		if ( hashIndex !== -1 ) {
			const hashFragment = url.substring( hashIndex + 1 );

			if ( hashFragment.startsWith( '#' ) ) {
				return hashFragment.substring( 1 );
			}
			return hashFragment;
		}

		return null;
	}

	windowEvents() {
		window.addEventListener( 'resize', this.debounce( () => {
			const ww = window.innerWidth;
			if ( this.windowWidth !== ww ) {
				this.checkHorizontalScroll();
				if ( ww > 991 ) {
					this.updateActiveLink();
				}
			}
		}, 100 ) );

		window.addEventListener( 'orientationchange', this.debounce( () => {
			const ww = window.innerWidth;
			this.checkHorizontalScroll();
			if ( ww > 991 ) {
				this.updateActiveLink();
			}
		}, 100 ) );

		window.addEventListener( 'hashchange', () => this.windowHashEvents() );
		window.addEventListener( 'scroll', this.debounce( () => {
			this.updateHash();
			this.updateActiveLink();
		}, 100 ) );
	}

	/**
	 * Update the hash with the closest element.
	 * @return {void}
	 */
	updateHash() {
		let hash = null;

		const element = this.findClosestElementOnScrollPosition( this.elements );
		if ( element ) {
			hash = element.id;
		}

		window.history.replaceState( null, null, !! hash ? `#${ hash }` : `${ window.location.origin }${ window.location.pathname }${ window.location.search }` );
	}

	/**
	 * Fire events based on window hash.
	 */
	windowHashEvents() {
		const hash = window.location.hash;
		if ( ! hash ) {
			return;
		}

		const element = document.getElementById( hash.slice( 1 ) );

		if ( ! element ) {
			return;
		}

		this.updateActiveLink();

		if ( ! this.isElementInViewport( element ) ) {
			element.scrollIntoView( {
				behavior: this.getScrollBehavior(),
				block: 'start',
			} );
		}
	}

	/**
	 * Update the active link state.
	 * @return {void}
	 */
	updateActiveLink() {
		const link = this.navigation.querySelector( `a[href="${ window.location.hash }"]` );
		const links = Array.from( this.links )?.filter( ( a ) => a !== link );

		if ( link ) {
			link.classList.add( 'active' );
			link.setAttribute( 'aria-current', 'true' );
		}

		links?.forEach( ( a ) => {
			a.classList.remove( 'active' );
			a.removeAttribute( 'aria-current' );
		} );
	}

	/**
	 * Find the closest element by scroll position.
	 *
	 * @param {HTMLCollection} elements
	 * @return {null}
	 */
	findClosestElementOnScrollPosition( elements ) {
		if ( 0 === elements.length ) {
			return null;
		}
		if ( 1 === elements.length ) {
			return elements[ 0 ];
		}

		let closestElement = null;
		let minDistance = Number.MAX_VALUE;

		Array.from( elements )?.forEach( ( element, index ) => {
			const rect = element.getBoundingClientRect();
			const distance = Math.abs( rect.top );

			if ( distance < minDistance && this.isElementInViewport( element ) ) {
				minDistance = distance;
				closestElement = element;
			}

			if ( elements.length - 1 === index && rect.bottom <= 0 ) {
				return null;
			}
		} );

		return closestElement;
	}

	/**
	 * Check if an element is in the viewport.
	 *
	 * @param {HTMLElement} element Element to check.
	 * @return {boolean}
	 */
	isElementInViewport( element ) {
		const rect = element.getBoundingClientRect();
		const windowHeight = ( window.innerHeight || document.documentElement.clientHeight );
		const offsetY = 0.1 * windowHeight;

		return rect.top - offsetY <= windowHeight && rect.bottom + offsetY >= 0;
	}

	/**
	 * Debounce function to prevent multiple calls.
	 *
	 * @param {function} func The function to fire.
	 * @param {int} delay The delay in milliseconds.
	 * @return {(function(): void)|*}
	 */
	debounce( func, delay = 250 ) {
		let timeoutId;
		return function () {
			clearTimeout( timeoutId );
			timeoutId = setTimeout( func, delay );
		};
	}

	/**
	 * Check if the links overflow horizontally.
	 *
	 * @return boolean
	 */
	checkHorizontalScroll() {
		let linksWidth = 0;
		const totalWidth = this.links?.map( ( link ) => linksWidth + link.offsetWidth ).reduce( ( accumulator, currentValue ) => accumulator + currentValue, 0 );

		const track = this.navigation.querySelector( '.fv-sticky-navigation__track' );

		if ( ! track ) {
			return false;
		}

		if ( track.offsetWidth > totalWidth && track.offsetWidth >= 480 ) {
			this.navigation.classList.add( 'fv-sticky-navigation--center' );
			return true;
		} else if ( this.navigation.classList.contains( 'fv-sticky-navigation--center' ) ) {
			this.navigation.classList.remove( 'fv-sticky-navigation--center' );
		}
		return false;
	}
}
